import { useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { DataTable } from '../../components/DataTable';
import { FilterSelect, SearchField } from '../../components/ListControls';
import { Panel } from '../../components/Panel';
import { SrbCode } from '../../components/SrbCode';
import { StatusChip } from '../../components/StatusChip';
import { toneColor } from '../../components/tone';
import { dmy } from '../../lib/dates';
import { statusOf, TYPE_TONE, type ReminderItem } from './model';

type Props = { items: ReminderItem[]; today: string; selectedId?: string; onSelect: (id: string) => void };

const STATUS_GROUPS = ['Overdue', 'Today', 'Upcoming', 'Scheduled', 'Completed'];
/** "In 4 days" belongs to the Upcoming group */
const group = (label: string) => (label.startsWith('In ') ? 'Upcoming' : label);

/** Every reminder in a table, with search and three filters */
export function ReminderTable({ items, today, selectedId, onSelect }: Props) {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [type, setType] = useState('all');
  const [shipment, setShipment] = useState('all');
  const w = search.trim().toLowerCase();
  const shown = items
    .filter((r) => status === 'all' || group(statusOf(r, today).label) === status)
    .filter((r) => type === 'all' || r.type === type)
    .filter((r) => shipment === 'all' || r.srb === shipment)
    .filter((r) => !w || [r.title, r.srb, r.type, r.relatedTo].join(' ').toLowerCase().includes(w));

  return (
    <Panel title="All Reminders">
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1}>
        <SearchField
          placeholder="Search reminders…"
          value={search}
          onChange={setSearch}
          sx={{ maxWidth: { lg: 320 } }}
        />
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          <FilterSelect label="Status" value={status} onChange={setStatus} options={STATUS_GROUPS} />
          <FilterSelect label="Type" value={type} onChange={setType} options={[...new Set(items.map((r) => r.type))]} />
          <FilterSelect
            label="Shipment"
            value={shipment}
            onChange={setShipment}
            options={[...new Set(items.map((r) => r.srb).filter(Boolean))].sort()}
          />
        </Stack>
      </Stack>
      <DataTable<ReminderItem>
        rows={shown}
        rowKey={(r) => r.id}
        empty="No reminders match."
        onRowClick={(r) => onSelect(r.id)}
        selected={(r) => r.id === selectedId}
        columns={[
          {
            header: 'Date',
            primary: true,
            nowrap: true,
            render: (r) => (
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: toneColor(TYPE_TONE[r.type]) }} />
                <span>{dmy(r.date)}</span>
              </Stack>
            ),
          },
          { header: 'Time', nowrap: true, render: (r) => r.time || '–' },
          {
            header: 'Title',
            render: (r) => (
              <Typography component="span" variant="body2" sx={{ fontWeight: 500 }}>
                {r.title}
              </Typography>
            ),
          },
          { header: 'Shipment', nowrap: true, render: (r) => (r.srb ? <SrbCode code={r.srb} /> : '–') },
          { header: 'Type', nowrap: true, render: (r) => r.type },
          { header: 'Related To', render: (r) => r.relatedTo },
          {
            header: 'Status',
            nowrap: true,
            action: true,
            render: (r) => {
              const s = statusOf(r, today);
              return <StatusChip label={s.label} tone={s.tone} />;
            },
          },
          { header: 'Created By', nowrap: true, render: (r) => r.createdBy },
        ]}
      />
    </Panel>
  );
}
